import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, X, Clock, Loader2 } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store';
import { uiActions } from '@/store/slices/uiSlice';
import { searchActions } from '@/store/slices/searchSlice';
import { buildPath } from '@/config/routes';
import { getWebsiteProductSearch } from '@/services/api/website-product.service';
import type { Product } from '@/types';

export function SearchOverlay() {
  const location = useLocation();
  const initialQuery = new URLSearchParams(location.search).get('q') || '';
  const [query, setQuery] = useState(initialQuery);
  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const recentSearches = useAppSelector((state) => state.search.recentSearches);

  useEffect(() => {
    inputRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const close = useCallback(() => {
    dispatch(uiActions.setSearchOpen(false));
  }, [dispatch]);

  const handleSearch = useCallback(
    (term: string) => {
      if (!term.trim()) return;
      dispatch(searchActions.addRecentSearch(term));
      navigate(buildPath.search(term));
      close();
    },
    [dispatch, navigate, close],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(query);
  };

  useEffect(() => {
    const searchTerm = query.trim();
    if (!searchTerm) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    let alive = true;
    setIsSearching(true);
    const timer = window.setTimeout(() => {
      getWebsiteProductSearch(searchTerm, 6)
        .then((items) => {
          if (alive) setSuggestions(items);
        })
        .catch(() => {
          if (alive) setSuggestions([]);
        })
        .finally(() => {
          if (alive) setIsSearching(false);
        });
    }, 250);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={close} />
      <div className="absolute inset-x-0 top-0 bg-white shadow-lg">
        <div className="mx-auto max-w-3xl px-4 py-4">
          <form onSubmit={handleSubmit} className="relative">
            <Search className="absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-text-muted" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for products, brands, sports..."
              className="h-12 w-full rounded-[var(--radius-md)] border border-border bg-bg-secondary pl-12 pr-12 text-sm focus:border-accent focus:outline-none"
            />
            <button
              type="button"
              onClick={close}
              className="absolute top-1/2 right-3 -translate-y-1/2 rounded-[var(--radius-sm)] p-1 text-text-muted hover:text-text-primary"
              aria-label="Close search"
            >
              <X className="h-5 w-5" />
            </button>
          </form>

          <div className="mt-4 max-h-[60vh] overflow-y-auto pb-4">
            {/* Recent Searches (only show when not typing) */}
            {!query.trim() && recentSearches.length > 0 && (
              <div className="mb-4">
                <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                  <Clock className="h-3.5 w-3.5" /> Recent Searches
                </h4>
                <div className="flex flex-wrap gap-2">
                  {recentSearches.slice(0, 6).map((term) => (
                    <button
                      key={term}
                      onClick={() => handleSearch(term)}
                      className="rounded-[var(--radius-full)] border border-border px-3 py-1.5 text-sm text-text-secondary hover:border-accent hover:text-accent"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Dynamic Suggestions */}
            {query.trim() && (
              <div>
                <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
                  <Search className="h-3.5 w-3.5" /> Product Suggestions
                </h4>
                {isSearching ? (
                  <div className="mt-4 flex items-center gap-2 text-sm text-text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" /> Searching products...
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {suggestions.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => {
                          close();
                          navigate(buildPath.product(product.slug));
                        }}
                        className="flex items-center gap-4 rounded-[var(--radius-md)] p-2 hover:bg-bg-secondary text-left transition-colors"
                      >
                        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-[var(--radius-sm)] bg-bg-tertiary">
                          <img src={product.thumbnail || product.images[0]} alt={product.name} className="h-full w-full object-cover" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-text-primary line-clamp-1">{product.name}</p>
                          <p className="text-xs text-text-secondary">{product.brand || product.category}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-text-muted mt-4">No products found for "{query}"</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



